import { Component, Inject, Input } from '@angular/core';

import { HttpErrorResponse } from "@angular/common/http";

import { Observable, BehaviorSubject, ReplaySubject, Subject, combineLatest, forkJoin, merge, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, startWith, tap, withLatestFrom } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { isSame, isSameArray } from '../../../../shared/basic-data-management/has-id.interface';
import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { LocalDate } from '../../../../shared/jsr310-localdates/types/local-date.type';
import { LocalTime } from '../../../../shared/jsr310-localdates/types/local-time.type';
import { LocalDateTime } from 'src/main/angular/app/shared/jsr310-localdates/types/local-date-time.type';
import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';
import { safeForkJoin } from '../../../../technical-modules/rxjs-helpers/rxjs-helpers';

import { AccesService } from '../services/acces.service';
import { AccesTLR } from '../entities/acces-tlr.entity';

import { OuvrageService } from '../../../structure/ouvrage/services/ouvrage.service';
import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';

import { AntenneService } from '../../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';

import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';
import { IntervenantTLR } from '../../../humain/intervenant/entities/intervenant-tlr.entity';

import { MemoService } from '../../../memo/services/memo.service';
import { Memo } from '../../../memo/entities/memo.entity';

import { MotifRefusService } from '../services/motif-refus.service';
import { MotifRefus } from '../entities/motif-refus.entity';

import { TypeHabilitationService } from '../../../habilitations/type-habilitation/services/type-habilitation.service';

import { ParcService } from '../../../structure/parc/services/parc.service';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';

import { UserService } from '../../../humain/user/services/user.service';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { AccesControleSecurite } from '../../../../static-types/entities/acces-controle-securite.entity';
import { IndicatifTelephone } from '../../../../static-types/entities/indicatif-telephone.entity';
import { NiveauAlerteIncendie } from '../../../../static-types/entities/niveau-alerte-incendie.entity';

import { CoactiviteEolienCheckCommand } from './coactivite-eolien-checker.component';
import { CoactiviteSolaireCheckCommand } from './coactivite-solaire-checker.component';

import { OuvrageBySousParcMultiSelectorWithNotesReinitData } from '../../../structure/ouvrage/components/ouvrage-by-sous-parc-multi-selector-with-notes.component';

import { BasicAccesOrder, ParticipantAcces } from '../entities/basic-acces-order.entity';
import { CoactiviteDecision } from '../entities/coactivite-decision.constants';
import { CreationAccesOrder } from '../entities/creation-acces-order.entity';
import { CreationDeniedAccesOrder } from '../entities/creation-denied-acces-order.entity';
import { CreationGrantedAccesOrder } from '../entities/creation-granted-acces-order.entity';
import { CreationPlannedAccesOrder } from '../entities/creation-planned-acces-order.entity';
import { EntrepriseAccesDTO } from '../entities/entreprise-acces-dto.entity';
import { IntervenantAccesDTO } from '../entities/intervenant-acces-dto.entity';
import { IntervenantRole } from 'src/main/angular/app/static-types/entities/intervenant-role.entity';
import { UpdateAccesOrder } from '../entities/update-acces-order.entity';
import { UpdateGrantedAccesOrder } from '../entities/update-granted-acces-order.entity';
import { UpdatePlannedAccesOrder } from '../entities/update-planned-acces-order.entity';
import { UpdateAndDenyAccesOrder } from '../entities/update-and-deny-acces-order.entity';
import { UpdateAndGrantAccesOrder } from '../entities/update-and-grant-acces-order.entity';
import { TypeHabilitationWithQualCodesDTO } from '../../../habilitations/type-habilitation/entities/type-habilitation-with-qual-codes-dto.entity';
import { TypeDocument } from '../../../humain/intervenant/components/intervenant-status.component';
import { applicationConstants, couleurIndicateurColors, ExpirationStatus } from '../../../../app.constants';

type DisplayedMemo = {
	entity: Memo,
	categorie: string,
}

type RadioOption<T> = {
	value: T,
	label: string,
}

class DisplayedIndicateur {
	nom!: string;
	icon!: string;
	couleur!: string;
	niveau!: string;
	regle!: string | null;
	dateModification!: LocalDateTime | null | undefined;
}

type IntervenantInput = {
	role: number | null,
	entreprise: EntrepriseTLR | null,
	intervenant: IntervenantTLR | null,
	modificationContacts: boolean,
	indicatifTelephone: IndicatifTelephone,
	telephone: string,
	email: string,
	shouldBlink: string,
	displayablePdpTypes: TypeDocument[]
}

type IntervenantRoleLabels = {
	[id: number]: string,
};

// Fonction d'aide utilisation dans les fonction d'enregistrement
let notNull = <T>(value: T | null, nom: string) => {
	if (value === null || value === undefined) {
		alert('Le champ ' + nom + ' ne peut pas être vide.');
		throw new Error('Le champ ' + nom + ' ne peut pas être vide.');
	}
	return value;
}

var arrayEquals = <T>(a: T[] | null, b: T[] | null) => {
	if (a === null) {
		return b == null;
	}

	if (b === null)
		return false;

	return a.length === b.length && a.every((val, index) => val === b[index]);
};

var isSolaire = (parc: ParcTLR | null | undefined) => !!parc && parc.energie === "Solaire";
var getAllowedClasses = (parc: ParcTLR) =>  parc.typeOnduleur ? ["PDL", "PDC", "GPO"] : ["PDL", "PDC", "OND", "PS"];

var allowLog = false;

var _logTap = <T> (label : string) => tap(
	(data: T) => {
		if (allowLog)
			console.log("edit-acces - " + label + ": ", data);
	}
);

@Component({
	selector: 'edit-acces',
	templateUrl: 'edit-acces.html',
	styleUrls: ['edit-acces.css'],
})
export class EditAccesComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	loaded = false;
	waitingForServer = false;
	antenneResetCommands!: Observable<number | null | undefined>;
	reinitOuvragesFieldCommands$ = new BehaviorSubject<OuvrageBySousParcMultiSelectorWithNotesReinitData | null>(null);
	modeSolaireLocal$!: Observable<boolean>;
	allowsOuvragesConcernes$!: Observable<boolean>;
	reinitOuvragesConcernesFieldCommands$ = new BehaviorSubject<OuvrageBySousParcMultiSelectorWithNotesReinitData | null>(null);
	getEntrepriseNom = (entreprise: EntrepriseTLR | null) => {
		if (!entreprise) {
			return '';
		}
		if (!entreprise.nom) {
			return '';
		}
		return entreprise.nom;
	};
	// NB: ici "null" = les valeurs n'ont pas encore ete chargee pour l'antenne selectionnee
	entreprisesById: MandatoryMap<number, EntrepriseTLR> | null = null;
	getIntervenantNom = (intervenant: IntervenantTLR | null) => {
		// Depuis le Fix 57776 cette fonction ne devrait plus jamais etre appelee
		// quand this.entreprisesById est null, dans le cas ou cela se produit,
		// generer une erreur pre-emptive
		if (!this.entreprisesById) {
			throw new Error();
		}
		
		if (!intervenant) {
			return '';
		}

		let elems: (string | null)[] = [];
		elems.push(intervenant.nom);
		elems.push(intervenant.prenom);

		// Nom entreprise
		if (intervenant.entrepriseId) {
			let entreprise = this.entreprisesById.get(intervenant.entrepriseId);
			let nomEntreprise = this.getEntrepriseNom(entreprise);
			if (nomEntreprise) {
				elems.push('(' + nomEntreprise + ')');
			}
		}

		return elems.filter($ => $ != null).join(' ');
	};
	getIndicatifLabel = (it: IndicatifTelephone) => it ? (it.name + ' (' + it.code + ') ' + it.dialCode) : '';
	resetDateCommands = new Subject<void>();
	private errorMessages: string[] = [];
	userCanGrantAcces$!: Observable<boolean>;
	checkCoactiviteEolienCommands = new Subject<CoactiviteEolienCheckCommand>();	
	checkCoactiviteSolaireCommands = new Subject<CoactiviteSolaireCheckCommand>();
	maxOuvrageSelectables$!: Observable<number | null>;

	// Listes d'autorite statiques a bind sur la vue
	indicatifTelephoneOptions: IndicatifTelephone[] = [];
	typeOptions: RadioOption<string>[] = [
		{
			value: 'LOCAL',
			label: 'Local',
		},
		{
			value: 'A_DISTANCE',
			label: 'A distance',
		},
	];
	ouiNonOptions: RadioOption<boolean>[] = [
		{
			value: true,
			label: 'Oui',
		},
		{
			value: false,
			label: 'Non',
		},
	];
	controlesSecurite$!: Observable<AccesControleSecurite[]>
	motifsRefus$!: Observable<MotifRefus[]>;
	intervenantRoles!: IntervenantRole[]; 
	intervenantRoleLabels: IntervenantRoleLabels = [];

	// Donnees dynamiques lier sur la vue
	title!: string;
	user!: UserTLR;
	mode!: string;
	acces!: AccesTLR | null;
	responsableParc!: IntervenantTLR | null;
	displayedMemosParc: DisplayedMemo[] = [];
	niveauAlertIncendie$: Observable<NiveauAlerteIncendie | null>;	
	indicateurs$!: Observable<DisplayedIndicateur[]>;
	entreprisesSelectionables: EntrepriseTLR[] = [];
	intervenantsSelectionables: IntervenantTLR[] = [];
	intervenantsSelectionablesByEntreprise: IntervenantTLR[][] = [];
	numero$ = new BehaviorSubject<string>('');
	dateTimeDebutJS$!: Observable<Date | null>;

	// Saisies de l'utilisateur
	antenne$ = new BehaviorSubject<AntenneTLR | null>(null);
	parc$ = new BehaviorSubject<ParcTLR | null>(null);
	ouvrages$ = new BehaviorSubject<OuvrageTLR[]>([]);
	ouvragesConcernes$ = new BehaviorSubject<OuvrageTLR[]>([]);
	intervenantsInputs$ = new BehaviorSubject<IntervenantInput[]>([]);
	typeAcces$ = new BehaviorSubject<string | null>(null);
	heureDebut$ = new ReplaySubject<LocalTime>(1);
	dateDebut$ = new BehaviorSubject<LocalDate | null>(null);
	commentaire!: string;
	lierIntervention!: boolean;
	accesCegex$ = new BehaviorSubject<boolean | null>(null);
	astreinte$ = new BehaviorSubject<boolean|null>(null);
	controleSecurite: number | null = null;
	motifRefus: number | null = null;
	isUserCac:boolean = false;
	isUserCc:boolean = false;
	
	// Information concatenee pour la saisie de la date/heure de debut
	dateHeureDebut$ = new BehaviorSubject<LocalDateTime | null>(null);
	
	constructor(
		private dialog: MatDialog,

		@Inject(EventBusService) private eventBusService: EventBusService,
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,
		@Inject(AccesService) private accesService: AccesService,
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) private intervenantService: IntervenantService,
		@Inject(MemoService) private memoService: MemoService,
		@Inject(MotifRefusService) motifRefusService: MotifRefusService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(OuvrageService) private ouvrageService: OuvrageService,
		@Inject(UserService) private userService: UserService,
		@Inject(TypeHabilitationService) private typeHabilitationService: TypeHabilitationService,
		@Inject(StaticTypeService) private staticTypeService: StaticTypeService,
	) {
		// Chargement des indicatifs de telephone
		staticTypeService.getIndicatifTelephoneList().subscribe(
			(indicatifs) => this.indicatifTelephoneOptions = indicatifs
		)
		
		/////////////////////////////////////////////////////////////////////////////////////////
		// Versions 'distinct' des observables de saisie, pour eviter les traitements doublons //
		/////////////////////////////////////////////////////////////////////////////////////////

		let distinctAntenne$ = this.antenne$.pipe(
			_logTap("antenne$"),
			distinctUntilChanged(isSame),
			_logTap("distinctAntenne$"),
		);

		let distinctParc$ = this.parc$.pipe(
			distinctUntilChanged(isSame),
		);

		let distinctOuvrages$ = this.ouvrages$.pipe(
			_logTap("ouvrages$"),
			distinctUntilChanged(isSameArray),
			_logTap("distinctOuvrages$"),
		);

		let distinctTypeAcces$ = this.typeAcces$.pipe(
			distinctUntilChanged(),
		);

		let distinctHeureDebut$ = this.heureDebut$.pipe(
			distinctUntilChanged(arrayEquals)
		);

		let distinctDateDebut$ = this.dateDebut$.pipe(
			distinctUntilChanged(arrayEquals)
		);

		let distinctAccesCegex$ = this.accesCegex$.pipe(
			distinctUntilChanged(),
		);

		//////////////////////////////////////////
		// Correspondances simple d'observables //
		//////////////////////////////////////////

		this.antenneResetCommands = distinctAntenne$.pipe(
			distinctUntilChanged(isSame),
			map($ => $ as ParcTLR | null),
			map(
				(antenne) => antenne == null ? null : antenne.id
			)
		);

		////////////////////////////////////////////////////////////////////////////////////
		// Lancement des pre-chargement de donnees "statiques" a la creation du composant //
		////////////////////////////////////////////////////////////////////////////////////

		// Chargement l'utilisateur courrant
		let user$ = userService.getCurrentUserTLR();

		// Chargement l'antenne de l'utilisateur
		let antenneOfUser$ = user$.pipe(
			mergeMap(
				(user) => user && user.role === 'CCE' ? of(null) : antenneService.getAntenneTLRById(user.antenneId)
			)
		);

		// Chargement des antennes
		let antennesById$ = antenneService.getAntenneTLRMapById().pipe(
			map(
				(map) => new MandatoryMap<number, AntenneTLR>(map, "Antenne")
			)
		);

		// Chargement des classes et sous-classes de memos
		let memoCategories$ = staticTypeService.getMemoCategoryMapById().pipe(
			map(
				(mcMap) => new MandatoryMap(mcMap, "MemoCategory")
			)
		);
		let memoSubCategories$ = staticTypeService.getMemoSubCategoryMapById().pipe(
			map(
				(mcMap) => new MandatoryMap(mcMap, "MemoSubCategory")
			)
		);

		// Chargement des classes d'ouvrages SOL et OND
		let classesOuvrages$ = ouvrageService.getClasseOuvrageList();
		let classesOuvragesMap$ = classesOuvrages$.pipe(
			map(
				(classes) => {
					let map = new Map<string, number>();
					for (let classe of classes) {
						map.set(classe.code, classe.id);
					}
					
					return new MandatoryMap<string, number>(map, "classeOuvrage");
				}
			)
		);

		// Chargement des controles de securite
		this.controlesSecurite$ = staticTypeService.getAccesControleSecuriteList();
		
		// Chargement des motifs de refus
		this.motifsRefus$ = motifRefusService.getList();
		
		// Chargement des niveaux d'alerte incendie
		let niveauxAlerteIncendie$ = staticTypeService.getNiveauAlerteIncendieMapById().pipe(
			map(
				(values) => new MandatoryMap<number, NiveauAlerteIncendie>(values, "NiveauAlerteIncendie")
			)
		);
		
		// Chargement des roles pour les intervenants
		let rolesIntervenants$ = staticTypeService.getIntervenantRoleList();

		/////////////////////////////////////
		// Definition des valeurs derivees //
		/////////////////////////////////////

		// La date complete de debut en version JS est calculee depuis l'heure et la date de debut au format Java
		let dateTimeDebut$ = combineLatest([distinctHeureDebut$, distinctDateDebut$]).pipe(map(
			(data) => {
				let [heureDebut, dateDebut] = data;

				if (dateDebut == null)
					return null;

				return conversionService.merge(dateDebut, heureDebut);
			}
		));
		dateTimeDebut$.subscribe(
			(date) => this.dateHeureDebut$.next(date)
		);
		this.dateTimeDebutJS$ = dateTimeDebut$.pipe(map(
			(dateTimeDebut) => {
				return conversionService.fromNullableLocalDateTime(dateTimeDebut);
			}
		));

		// L'astreinte est une donne derivee de la date+heure de debut
		this.dateTimeDebutJS$.subscribe(
			(dateTimeDebutJS) => {
				let dateRef = dateTimeDebutJS ? dateTimeDebutJS : new Date();

				let jourSemaine = dateRef.getDay(); // 0 = Dimanche
				let jourOuvre = jourSemaine >= 1 && jourSemaine <= 5;
				let heure = dateRef.getHours();
				let heureOuvree = heure >= 8 && heure < 18;
				
				this.astreinte$.next(!jourOuvre || !heureOuvree);
			}
		);

		// Les memos du parc sont fonction du parc selectionne et de la date de debut
		let memosParc$ = combineLatest([distinctParc$, distinctDateDebut$]).pipe(mergeMap(
			(data) => {
				let [parc, dateRef] = data;

				// En cas d'absence d'un des deux parametres, on considere qu'il n'y a pas de memo
				if (!parc || !dateRef) {
					return of([] as Memo[]);
				}

				let dateParam = this.conversionService.toLocalDateParam2(dateRef);
				return this.memoService.getMemosOnParcForDate(parc.id, dateParam);
			}
		));

		// L'affichage des memos du parc depends de ces memo et des proprietes des categories/sous-categogies
		// (Par souci de simplicite d'ecriture cote vue, on fige le resultats en tant que propriete
		// plutot que faire un autre observable. Ce qui ne pose pas de souci tant qu'on ne fait rien d'autre
		// avec ces memos que les afficher)
		combineLatest([memosParc$, memoCategories$, memoSubCategories$]).subscribe(
			(data) => {
				let [memos, memoCategories, memoSubCategories] = data;

				let sortedMemos = memos.sort((a, b) => a.niveauId - b.niveauId);
				this.displayedMemosParc = sortedMemos.map(
					(memo) => {
						let categorie = '';

						if (memo.categorieId) {
							categorie = memoCategories.get(memo.categorieId).label;
							if (memo.sousCategorieId) {
								categorie = categorie + ' (' + memoSubCategories.get(memo.sousCategorieId).label + ') '
							}
							categorie += ' - ';
						}

						return {
							entity: memo,
							categorie: categorie,
						} as DisplayedMemo;
					}
				);
			}
		);

		// Lors d'un changement d'antenne
		let newNumeroAcces$ = distinctAntenne$.pipe(
			filter(
				(_antenne) => this.acces === null
			),
			mergeMap(
				(antenne) => antenne ? accesService.getNumeroAcces(antenne.id) : of('')
			),
			_logTap("newNumeroAcces$"),
		);
		newNumeroAcces$.subscribe(
			(newNumero) => this.numero$.next(newNumero)
		);

		let entreprises$ = entrepriseService.getActiveEntreprisesTLRList();
		let intervenants$ =intervenantService.getActiveIntervenantsTLR();
        combineLatest([entreprises$, intervenants$]).pipe(
            map(
                ([entreprises, intervenants]) => {
						let entrepriseHasIntervenants = new Map<number, boolean>();
						intervenants
							// NB: ici on sait normalement que entrepriseId n'est pas null
							// car les intervenants ont ete recuperes a travers les entreprises
							.map($ => $.entrepriseId as number)
							.forEach($ => entrepriseHasIntervenants.set($, true))
							;

						let entreprisesSelectionables = entreprises.filter($ => entrepriseHasIntervenants.get($.id));
						return {
							entreprises: entreprisesSelectionables,
							intervenants: intervenants,
						};
					}
                )
		).pipe(
			map(
				({
					entreprises,
					intervenants,
				}) => {
					let entrepriseHasIntervenants = new Map<number, boolean>();
					intervenants
						// NB: ici on sait normalement que entrepriseId n'est pas null
						// car les intervenants ont ete recuperes a travers les entreprises
						.map($ => $.entrepriseId as number)
						.forEach($ => entrepriseHasIntervenants.set($, true))
						;

					let entreprisesSelectionables = entreprises.filter($ => entrepriseHasIntervenants.get($.id));
					return {
						entreprises: entreprisesSelectionables,
						intervenants: intervenants,
					};
				}
			)
		).subscribe(
			({
				entreprises,
				intervenants,
			}) => {
				// Preparation de la liste entreprises et de la map utilisee pour les libelles d'intervenants				
				let mapEnt = new Map<number, EntrepriseTLR>();
				entreprises.forEach(
					(entreprise) => mapEnt.set(entreprise.id, entreprise)
				);
				this.entreprisesSelectionables = entreprises;
				this.entreprisesById = new MandatoryMap<number, EntrepriseTLR>(mapEnt, "Entreprise");

				// Prepation de la liste des intervenants pour l'antenne et pour chaque entreprise
				let mapInt: IntervenantTLR[][] = [];
				intervenants.forEach(
					(intervenant) => {
						// NB: ici on sait normalement que entrepriseId n'est pas null
						// car les intervenants ont ete recuperes a travers les entreprises
						let entrepriseId = intervenant.entrepriseId as number;
						let intervenantsOfEntreprise = mapInt[entrepriseId];
						if (!intervenantsOfEntreprise) {
							intervenantsOfEntreprise = [] as IntervenantTLR[];
							mapInt[entrepriseId] = intervenantsOfEntreprise;
						}
						intervenantsOfEntreprise.push(intervenant);
					}
				);
				this.intervenantsSelectionables = intervenants;
				this.intervenantsSelectionablesByEntreprise = mapInt;
			}
		);
		
		// Sert a n'avoir que les transitions entre si le type d'acces est actuellement distant
		// ou non. Cela permet de ne pas declencher d'evenements inutiles quand l'utilisateur
		// passe de typeAcces = null  a typeAcces = 'local'
		let isAccesDistant$ = distinctTypeAcces$.pipe(
			map(
				(typeAcces) => typeAcces === "A_DISTANCE"
			),
			distinctUntilChanged(),
		);
		
		// Niveau d'alerte incendie
		this.niveauAlertIncendie$ = combineLatest([niveauxAlerteIncendie$, this.parc$]).pipe(
			map(
				([niveauxAlerteIncendie, parc]) => {
					if (parc === null) {
						return null;
					}
					
					if (parc.niveauIncendie == null) {
						return null;
					}
					
					return niveauxAlerteIncendie.get(parc.niveauIncendie);
				}
			)
		);
		
		this.indicateurs$ = distinctParc$.pipe(
					mergeMap(
						(parc) => parc == null || parc == undefined ? of([]) : parcService.getParcIndicateurs(parc.id)
					),
					map(
						(indicateurs) => indicateurs
							.filter($ => $.actif !== false)
							.map(
								($) => {
									let config = applicationConstants.indicateurs[$.indicateurCode];
									return {
										nom: config ? config.nom : $.indicateurNom,
										icon: config ? config.icon : 'fa-solid fa-circle-question',
										couleur: couleurIndicateurColors[$.couleur] ?? '#b0b0b0',
										niveau: $.couleur,
										regle: $.regle,
										dateModification: $.dateModification
									} as DisplayedIndicateur;
								}
							)
					)
				);


		////////////////////////////////////////////////////////////////////
		// Petite action utilisateur aussi utilisee dans l'initialisation //
		////////////////////////////////////////////////////////////////////

		this.resetDateCommands.subscribe(
			() => {
				// Evol 54010: On ne fait plus l'arrondi aux 5mn les plus proches
				// let dateDebutJS = dateService.getCurrentRoundedDateAndTime();
				let dateDebutJS = new Date();
				dateDebutJS.setSeconds(0);
				dateDebutJS.setMilliseconds(0);
				
				this.dateDebut$.next(this.conversionService.toMandatoryLocalDate(dateDebutJS));
				this.heureDebut$.next(this.conversionService.toMandatoryLocalTime(dateDebutJS));
			}
		);

		///////////////////////////////////////////////
		// Calculs de validation sur les observables //
		///////////////////////////////////////////////

		// NB: Ces controles sont ceux qu'on pourrait afficher dans tous les cas, des la saisie du champ
		// pour les controles specifiques a une operation, voir la fonction de validation ad-hoc 

		let errorMessages$: Observable<string | null>[] = [];

		let parcErrorMessage$ = distinctParc$.pipe(
			map(
				(parc) => parc === null ? "Veuillez sélectionner un parc." : null
			)
		);
		errorMessages$.push(parcErrorMessage$);
		
		let ouvragesErrorMessage$ = distinctOuvrages$.pipe(
			map(
				(ouvrages) => (ouvrages === null || ouvrages.length === 0) ? "Veuillez sélectionner un ouvrage." : null
			)
		);
		errorMessages$.push(ouvragesErrorMessage$);

		let entrepriseMissing$ = this.intervenantsInputs$.pipe(
			map(
				(inputs) => {
					return inputs.some($ => $.entreprise === null);
				}
			)
		);
		let intervenantInputsErrorMessage$ = entrepriseMissing$.pipe(
			map(
				(missing) => missing ? "Vous devez renseigner l'entreprise de chaque intervenant." : null
			)
		);
		errorMessages$.push(intervenantInputsErrorMessage$);

		let entreprisesDoublons$ = this.intervenantsInputs$.pipe(
			map(
				(inputs) => {
					let entreprisesId = inputs
						.filter($ => $.intervenant == null)
						.map($ => $.entreprise)
						.filter($ => $)
						// Du au filtre precedent, la valeur -1 ne devrai jamais sortir
						.map($ => $ === null ? -1 : $.id)
						;

					let known = new Map<number, boolean>();
					for (let entrepriseId of entreprisesId) {
						if (known.get(entrepriseId)) {
							return true;
						}
						known.set(entrepriseId, true);
					}

					return false;
				}
			)
		);
		let entreprisesDoublonsErrorMessage$ = entreprisesDoublons$.pipe(
			map(
				(missing) => missing ? "Vous ne devez pas avoir d'entreprises en double." : null
			)
		);
		errorMessages$.push(entreprisesDoublonsErrorMessage$);

		let intervenantsDoublons$ = this.intervenantsInputs$.pipe(
			map(
				(inputs) => {
					let intervenantsId = inputs
						.map($ => $.intervenant)
						.filter($ => $)
						// Du au filtre precedent, la valeur -1 ne devrai jamais sortir
						.map($ => $ === null ? -1 : $.id)
						;

					let known = new Map<number, boolean>();
					for (let intervenantId of intervenantsId) {
						if (known.get(intervenantId)) {
							return true;
						}
						known.set(intervenantId, true);
					}

					return false;
				}
			)
		);
		let intervenantsDoublonsErrorMessage$ = intervenantsDoublons$.pipe(
			map(
				(missing) => missing ? "Vous ne devez pas avoir d'intervenants en double." : null
			)
		);
		errorMessages$.push(intervenantsDoublonsErrorMessage$);
		
		// Evol 57237 - RG2 : Les valeurs “Chargé d’accès” et “Chargé de travaux (électrique)” doivent être unique (ne peuvent être saisies qu'une seule fois)
		let chargeAccesMultiple$ = this.intervenantsInputs$.pipe(
			map(
				(inputs) => {
					let count = inputs
						.filter($ => $.role === 0)
						.length
					;

					return count > 1;
				}
			)
		);
		let chargeAccesMultipleErrorMessage$ = chargeAccesMultiple$.pipe(
			map(
				(doublon) => doublon ? "Vous ne devez pas avoir plus d'un chargé d'accès." : null
			)
		);
		errorMessages$.push(chargeAccesMultipleErrorMessage$);
		let chargeTravauxElecMultiple$ = this.intervenantsInputs$.pipe(
			map(
				(inputs) => {
					let count = inputs
						.filter($ => $.role === 2)
						.length
					;

					return count > 1;
				}
			)
		);
		let chargeTravauxElecMultipleErrorMessage$ = chargeTravauxElecMultiple$.pipe(
			map(
				(doublon) => doublon ? "Vous ne devez pas avoir plus d'un chargé de travaux." : null
			)
		);
		errorMessages$.push(chargeTravauxElecMultipleErrorMessage$);

		let typeAccesErrorMessage$ = distinctTypeAcces$.pipe(
			map(
				(typeAcces) => typeAcces == null ? "Veuillez choisir un type." : null
			)
		);
		errorMessages$.push(typeAccesErrorMessage$);

		let dateDebutErrorMessage$ = this.dateTimeDebutJS$.pipe(
			map(
				(dateDebut) => dateDebut === null ? "Veuillez renseigner complètement la date de début." : null
			)
		);
		errorMessages$.push(dateDebutErrorMessage$);

		let accesCegexErrorMessage$ = distinctAccesCegex$.pipe(
			map(
				(accesCegex) => accesCegex == null ? "Veuillez remplir la valeur pour \"Accès Cegex\"." : null
			)
		);
		errorMessages$.push(accesCegexErrorMessage$);

		combineLatest(errorMessages$).subscribe(
			(errorMessages: (string | null)[]) => this.errorMessages = errorMessages.filter($ => $ != null) as string[]
		);

		/////////////////////////////////////////////////////////
		// Calcul des autorisation d'utilisation des fonctions //
		/////////////////////////////////////////////////////////

		let userResponsibilitiesOnSelectedOuvrages$ = combineLatest([user$, distinctOuvrages$]).pipe(
			map(
				(data) => {
					let [user, ouvrages] = data;
					return {
						isCac: ouvrages.length > 0 && !ouvrages.some($ => $.cacId != user.id),
						isCc: ouvrages.length > 0 && !ouvrages.some($ => $.ccId != user.id)
					};
				}
			)
		);
		this.userCanGrantAcces$ = combineLatest([user$, userResponsibilitiesOnSelectedOuvrages$]).pipe(
			map(
				(data) => {
					let [user, userResponsibilitiesOnSelectedOuvrages] = data;
					this.isUserCac = userResponsibilitiesOnSelectedOuvrages.isCac;
					this.isUserCc = userResponsibilitiesOnSelectedOuvrages.isCc;
					// Il n'est possible d'autoriser un acces que si l'utilisateur connecte est considere comme CAC ou CC par rapport a l'ensemble du perimetre demande dans l'acces
					return (user.role === 'EXP' || user.role === 'CCE') && userResponsibilitiesOnSelectedOuvrages.isCac || (userResponsibilitiesOnSelectedOuvrages.isCc && this.typeAcces$.getValue() === 'A_DISTANCE');
				}
			)
		);

		//////////////////////////////////////////////
		// Evenements de saisie sur des observables //
		//////////////////////////////////////////////

		distinctAntenne$.subscribe(
			(_antenne) => {
				this.parc$.next(null);
				//this.resetIntervenantsInputs();
			}
		);

		// Remise en place des biding antenne et responsable quand le parc change
		distinctParc$.pipe(
			withLatestFrom(distinctAntenne$, antennesById$)
		).subscribe(
			(data) => {
				let parc = data[0];
				let antenne = data[1];
				let antennesById = data[2];

				this.responsableParc = null;
				this.ouvragesConcernes$.next([]);

				if (parc && parc.responsableId) {
					// Verifier si l'antenne est selectionnee, sinon, le faire automatiquement
					// (cf Redmine #33419 RG2)
					if (antenne == null) {
						let antenneOfParc = antennesById.get(parc.antenneId);
						this.antenne$.next(antenneOfParc);
					}

					// Chargement des donnees sur le responsable du parc
					this.intervenantService.getIntervenantTLRById(parc.responsableId).subscribe(
						(intervenant) => this.responsableParc = intervenant
					);
				}
			}
		);
		
		/////////////////////////////////
		// Gestion du champ "Ouvrages"
		
		this.maxOuvrageSelectables$ = combineLatest([distinctParc$, isAccesDistant$]).pipe(
			map(
				([parc, isAccesDistant]) => (isSolaire(parc) && isAccesDistant) ? null : 1
			),
			startWith(1),
		);
		
		this.modeSolaireLocal$ = combineLatest([distinctParc$, isAccesDistant$]).pipe(
			map(
				([parc, isAccesDistant]) =>  isSolaire(parc) && !isAccesDistant
			),
			startWith(false),
			_logTap("modeSolaireLocal$"),
		);
		
		// Cas 1 - Quand l'utilisateur selectionne un parc non-solaire
		let parcSelectedEvents$ = distinctParc$.pipe(
			filter(
				(parc) => !!parc
			),
			map(
				(parc) => parc as ParcTLR
			),
		);
		let reinitOuvragesFieldEvent1$ = parcSelectedEvents$.pipe(
			filter(
				(parc) => !isSolaire(parc)
			),
			map(
				// Pour ce cas, on ne filtre aucun ouvrage et on ne selectionne rien
				(parcNonSolaire) => ({
					parc: parcNonSolaire,
					selectedOuvrageIds: [],
				} as OuvrageBySousParcMultiSelectorWithNotesReinitData)
			),
			_logTap("reinitOuvragesFieldEvent1$"),
		);

		// Cas 2 - A: Quand l'utilisateur selectionne un parc solaire et que le type d'acces est local
		//      OU B: quand l'utilisateur slectionner le type d'acces "local" alors qu'un parc solaire est selectionne
		let parcSolaireSelectedEvent$ = parcSelectedEvents$.pipe(
			filter(
				(parc) => isSolaire(parc)
			),
			withLatestFrom(isAccesDistant$),
			map(
				([parc, isAccesDistant]) => ({
					parcSolaire: parc,
					isAccesDistant: isAccesDistant,
				})
			)
		);
		let reinitOuvragesFieldEvent2A$ = parcSolaireSelectedEvent$.pipe(
			filter(
				(data) => !data.isAccesDistant
			),
			map(
				(data) => data.parcSolaire
			),
			_logTap("reinitOuvragesFieldEvent2A$"),
		);
		let reinitOuvragesFieldEvent2B$ = isAccesDistant$.pipe(
			withLatestFrom(this.parc$),
			filter(
				([isAccesDistant, parc]) => !isAccesDistant && isSolaire(parc) 
			),
			map(
				(data) => data[1] as ParcTLR
			),
			_logTap("reinitOuvragesFieldEvent2B$"),
		);
		let reinitOuvragesFieldEvent2$ = merge(reinitOuvragesFieldEvent2A$, reinitOuvragesFieldEvent2B$).pipe(
			withLatestFrom(classesOuvragesMap$),
			map(
				([parcSolaire, classeOuvragesMap]) => {
					let classeSOL = classeOuvragesMap.get("SOL");
					
					// Pour ce cas, on selectionne et n'autorise que les ouvrages SOL
					// NB: la saisie du champ par l'utilisateur est normalement desactivee
					return {
						parc: parcSolaire,
						ouvrageFilter: (_parc: ParcTLR, ouvrage: OuvrageTLR) => ouvrage.classeOuvrage === classeSOL,
						ouvrageSelector: (ouvrage: OuvrageTLR) => ouvrage.classeOuvrage === classeSOL,
					} as OuvrageBySousParcMultiSelectorWithNotesReinitData;
				}
			),
			_logTap("reinitOuvragesFieldEvent2$"),
		);
		
		// Cas 3 - A: Quand l'utilisateur selectionne un parc solaire et que le type d'acces est distant
		//      OU B: Quand l'utilisateur selectionne le type d'acces distant et qu'un parc solaire est selectionne
		let reinitOuvragesFieldEvent3A$ = parcSolaireSelectedEvent$.pipe(
			filter(
				(data) => data.isAccesDistant
			),
			map(
				(data) => data.parcSolaire
			),
			_logTap("reinitOuvragesFieldEvent3A$"),
		);
		let reinitOuvragesFieldEvent3B$ = isAccesDistant$.pipe(
			withLatestFrom(this.parc$),
			filter(
				([isAccesDistant, parc]) => isAccesDistant && isSolaire(parc) 
			),
			map(
				([_isAccesDistant, parc]) => parc as ParcTLR
			),
			_logTap("reinitOuvragesFieldEvent3B$"),
		);
		let reinitOuvragesFieldEvent3$ = merge(reinitOuvragesFieldEvent3A$, reinitOuvragesFieldEvent3B$).pipe(
			withLatestFrom(classesOuvragesMap$),
			map(
				([parcSolaire, classeOuvragesMap]) => {
					let allowedClasses = getAllowedClasses(parcSolaire);
					let allowedClassesIds = allowedClasses.map($ => classeOuvragesMap.get($));
					let ouvrageFilter = (_parc: ParcTLR, ouvrage: OuvrageTLR) => {
							let classe = ouvrage.classeOuvrage;
							return allowedClassesIds.some($ => $ === classe);
					};

					// Pour ce cas, on n'autorise que les ouvrages adapates au type d'onduleur
					// Et on tente de reselectionner les ouvrages precedemment selectionnes					
					return {
						parc: parcSolaire,
						ouvrageFilter: ouvrageFilter,
						selectedOuvrageIds: [],
					} as OuvrageBySousParcMultiSelectorWithNotesReinitData;
				}
			),
			_logTap("reinitOuvragesFieldEvent3$"),
		);

		// Transmission de la commande de reinitialisation au composant fils		
		let reinitOuvragesFieldEvents$ = merge(
			reinitOuvragesFieldEvent1$,
			reinitOuvragesFieldEvent2$,
			reinitOuvragesFieldEvent3$,
		);
		reinitOuvragesFieldEvents$.subscribe(
			(reinitData) => {
				// Uniquement si le composant a fini de se charger, on transmet
				// la commande de reinitialisation
				// (sinon c'est que les changements sont programmatifs et on va
				// attendre d'avoir aussi les ouvrages a preselectionner
				// -voir plus bas-)
				if (this.loaded) {
					this.reinitOuvragesFieldCommands$.next(reinitData);
				}
			}
		);

		// Si on tente d'initialiser les ouvrages programmativement
		let ouvragesSelectionCommands$ = new Subject<OuvrageTLR[]>();
		let programativeOuvragesSelectionEvents$ = ouvragesSelectionCommands$.pipe(
			// Alors on recupere la derniere commande d'initialisation
			withLatestFrom(reinitOuvragesFieldEvents$),
			// On la modifie pour inclure les ouvrages
			map(
				([ouvrages, reinitData]) => {
					reinitData.selectedOuvrageIds = ouvrages.map($ => $.id);
					reinitData.ouvrageSelector = undefined;
					return reinitData;
				}
			),
			_logTap("programativeOuvragesSelectionEvents$"),
		);
		programativeOuvragesSelectionEvents$.subscribe(
			(reinitData) => this.reinitOuvragesFieldCommands$.next(reinitData)
		);
	
		//////////////////////////////////////////
		// Gestion du champ "Ouvrages Concernes"
		
		let ouvragesConcernesRules$ = combineLatest([distinctParc$, isAccesDistant$, distinctOuvrages$]).pipe(
			withLatestFrom(classesOuvragesMap$),
			map(
				([[parc, isAccesDistant, ouvrages], classesOuvragesMap]) => {
					// Les parcs non-solaires centralisés n'ont pas d'ouvrages concernes
					if (!isSolaire(parc)) {
						return {
							display: false,
							filter: (_parc: ParcTLR, _ouvrage: OuvrageTLR) => false,
						};
					}
					
					// Les parcs solaires en acces local ont droit a des ouvrages concernes
					if (!isAccesDistant) {
						let classeSOL = classesOuvragesMap.get("SOL");
						return {
							display: true,
							filter: (_parc: ParcTLR, ouvrage: OuvrageTLR) => ouvrage.classeOuvrage != classeSOL
						}
					}
					
					// Evol 52295
					// Pour les parcs solaires en acces distant, cela depend s'il y a des ouvrages GPO selectionnes
					let classeGPO = classesOuvragesMap.get("GPO");
					let ouvragesGPO = ouvrages.filter($ => $.classeOuvrage === classeGPO);
					if (ouvragesGPO.length === 0) {
						return {
							display: false,
							filter: (_parc: ParcTLR, _ouvrage: OuvrageTLR) => false,
						}
					}
					let ouvragesGPOIds = ouvragesGPO.map($ => $.id);
					return {
						display: true,
						filter: (_parc: ParcTLR, ouvrage: OuvrageTLR) => {
							return ouvragesGPOIds.some($ => $ === ouvrage.ouvrageElecPere);
						}
					};
				}
			),
			_logTap("ouvragesConcernesRules$"),
		);
		
		this.allowsOuvragesConcernes$ = ouvragesConcernesRules$.pipe(
			map(
				(rules) => rules.display
			)
		);
		
		// Quand on masque le champ ouvrage concerne, on le vide, pour etre sur
		// qu'il ne restera aucune reference lors d'une tentative d'enregistrement
		let ouvragesConcernesFieldDisabled$ = this.allowsOuvragesConcernes$.pipe(
			distinctUntilChanged(),
			filter(
				(display) => !display
			),
		);
		ouvragesConcernesFieldDisabled$.subscribe(
			() => {
				if (this.loaded) {
					this.ouvragesConcernes$.next([]);
				}
			}
		);
		
		// Reinitialisation du champ
		let reinitOuvragesConcernesFieldEvent$ = ouvragesConcernesRules$.pipe(
			filter(
				(rules) => rules.display
			),
			map(
				(rules) => rules.filter
			),
			withLatestFrom(this.parc$),
			filter(
				([_filter, parc]) => parc != null
 			),
 			_logTap("reinitOuvragesConcernesFieldEvent$"),
		);
		reinitOuvragesConcernesFieldEvent$.subscribe(
			([filter, parc]) => {
				// Uniquement si le composant a fini de se charger, on transmet
				// la commande de reinitialisation
				// (sinon c'est que les changements sont programmatifs et on va
				// attendre d'avoir aussi les ouvrages concernes a preselectionner
				// -voir plus bas-)
				if (this.loaded) {
					this.reinitOuvragesConcernesFieldCommands$.next({
						parc: parc as ParcTLR,
						ouvrageFilter: filter,
						selectedOuvrageIds: [],
					});
				}
			}
		);

		// Si on tente d'initialiser les ouvrages concernes programmativement
		let ouvragesConcernesSelectionCommands$ = new Subject<OuvrageTLR[]>();
		let programativeOuvragesConcernesSelectionEvents$ = ouvragesConcernesSelectionCommands$.pipe(
			// Alors on recupere la derniere commande d'initialisation
			withLatestFrom(reinitOuvragesConcernesFieldEvent$),
			_logTap("programativeOuvragesConcernesSelectionEvents$"),
		);
		programativeOuvragesConcernesSelectionEvents$.subscribe(
			([ouvragesConcernes, [filter, parc]]) => {
				this.reinitOuvragesConcernesFieldCommands$.next({
					parc: parc as ParcTLR,
					ouvrageFilter: filter,
					selectedOuvrageIds: ouvragesConcernes.map($ => $.id),
				});
			}
		);

		///////////////////////////////////////////////////////////////////////////////////////////////////////
		// Initialisation du composant lors de son affichage, a partir des informations de contexte fournies //
		///////////////////////////////////////////////////////////////////////////////////////////////////////
		
		////////////////////////////////////
		// Initialisaiton: ELEMENT COMMUNS
		
		// Binding de l'utilisateur et des roles d'intervenants, servant de pre-requis a toutes initialisation

		let staticDataLoaded$ = combineLatest([user$, rolesIntervenants$]).pipe(
			tap(
				([user, rolesIntervenants]) => {
					this.user = user;

					this.intervenantRoles = rolesIntervenants;
					rolesIntervenants.forEach($ => {
						let id = $.id;
						this.intervenantRoleLabels[id] = $.label;
					});
				}
			),
			map(
				(_data) => {}
			)
		);
		
		// Operateurs nettoyant la vue des elements qui ont pu y etre mis par l'utilisateur precedent
		let clearViewBindings = () => {
			this.mode = "#error#";
			this.title = "#error#";
			this.acces = null;

			this.numero$.next('');
			this.antenne$.next(null);
			this.parc$.next(null);
			this.ouvrages$.next([]);
			this.ouvragesConcernes$.next([]);
			this.resetIntervenantsInputs();
			this.typeAcces$.next(null);
			this.resetDateCommands.next();
			this.commentaire = '';
			this.lierIntervention = true;
			this.accesCegex$.next(null);
			this.motifRefus = null;
			this.controleSecurite = null;
			this.isUserCac = false;
			this.isUserCc = false;
			
			this.loaded = false;
		};

		
		// Operateurs utilises pour charge les donnees associes a un numero d'acces
		let loadAccesOperator = mergeMap(
			(accesId: number) => accesService.getById(accesId)
		);
		let loadFirstLevelDependenciesOperator = mergeMap(
			(acces: AccesTLR) => {
				let parc$ = parcService.getParcTLRById(acces.parcId);
				let ouvrages$ = ouvrageService.getOuvrageTLRByAcces(acces.id);
				let intervenants$ = accesService.getIntervenantsForAcces(acces.id);
				let entreprises$ = accesService.getEntreprisesForAcces(acces.id);
				
				return forkJoin({
					acces: of(acces),
					parc: parc$,
					ouvrages: ouvrages$,
					intervenants: intervenants$,
					entreprises: entreprises$,
				});
			}
		);
		let loadSecondLevelDependenciesOperator = mergeMap(
			(accesData: {
				acces: AccesTLR,
				parc: ParcTLR,
				ouvrages: OuvrageTLR[],
				intervenants: IntervenantAccesDTO[],
				entreprises: EntrepriseAccesDTO[],
			}) => {
				let acces = accesData.acces;
				let parc = accesData.parc;
				let ouvrages = accesData.ouvrages;
				let intervenants = accesData.intervenants;
				let entreprises = accesData.entreprises;

				let antenne$ = antenneService.getAntenneTLRById(parc.antenneId);
				let ouvragesConcernes$ = isSolaire(parc) ?
					this.ouvrageService.getOuvrageConcerneTLRByAcces(acces.id) :
					of([] as OuvrageTLR[]);

				// On charge ici les entreprises liees aux intervenants
				// Ceci est necessaire pour etre sure d'avoir les donnes des l'initialisation
				// et ne pas a avoir a attendre que le chargement de l'entreprise par
				// l'antenne aie lieu
				let intervenantsAvecEntreprises$ = intervenants.map(intervenant => {
					return forkJoin({
						intervenant: of(intervenant),
						// NB: ici on sait normalement que entrepriseId n'est pas null
						// car les intervenants ont ete recuperes a travers les entreprises
						entreprise: entrepriseService.getActiveEntrepriseTLRById(intervenant.entrepriseId as number),
					});
				});

				return forkJoin({
					acces: of(acces),
					antenne: antenne$,
					parc: of(parc),
					ouvrages: of(ouvrages),
					ouvragesConcernes: ouvragesConcernes$,
					intervenants: safeForkJoin(intervenantsAvecEntreprises$),
					entreprises: of(entreprises),
				});
			}
		);
		
		// Operateur utilise pour charger les champs avec les donnees d'un acces
		let bindAccesData = tap(
			(accesData: {
				acces: AccesTLR,
				antenne: AntenneTLR,
				parc: ParcTLR,
				ouvrages: OuvrageTLR[],
				ouvragesConcernes: OuvrageTLR[],
				intervenants: {
					intervenant: IntervenantAccesDTO,
					entreprise: EntrepriseTLR,
				}[],
				entreprises: EntrepriseAccesDTO[],
			}) => {
				let acces = accesData.acces;
				let antenneOfAcces = accesData.antenne;
				let parcOfAcces = accesData.parc;
				let ouvrages = accesData.ouvrages;
				let ouvragesConcernes = accesData.ouvragesConcernes;
				let intervenants = accesData.intervenants;
				let entreprises = accesData.entreprises;

				// Initialisation du bloc perimetre
				this.antenne$.next(antenneOfAcces);
				this.parc$.next(parcOfAcces);

				// ATTENTION: depuis l'evol 52295, il faut remplir le type d'acces
				// avant de toucher aux ouvrages et ouvrages concernes
				this.typeAcces$.next(this.typeOptions[acces.typeAcces].value);

				// Petite astuce ici: alimenter en avance la variable ouvrages$
				// En effet, ouvragesSelectionCommands$.next ne mettra a jour
				// cette variable que de facon asynchrone, cependant, pour initialiser
				// correctement le champ ouvrage concernes, il faut que la valeur
				// ouvrages$ soit correctement positionnee
				this.ouvrages$.next(ouvrages);

				// Suite de l'initialisation du bloc perimetre
				ouvragesSelectionCommands$.next(ouvrages);
				ouvragesConcernesSelectionCommands$.next(ouvragesConcernes);

				// Initialisation du bloc intervenants
				let inputs = [] as IntervenantInput[];
				
				// Evol 57237: Il faut presenter les intervenants dans l'ordre imposes par les roles
				for (let role of this.intervenantRoles) {
					let roleId = role.id;
					
					let intervenantsForRole = intervenants.filter($ => $.intervenant.roleIntervenant === roleId);
					intervenantsForRole.forEach($ => {
						let input = {
							role: roleId,
							entreprise: $.entreprise,
							intervenant: $.intervenant as IntervenantTLR,
							modificationContacts: false,
						} as IntervenantInput;
						this.updateContactDisplay(input);
						inputs.push(input);
					});
					
					let entreprisesForRole = entreprises.filter($ => $.roleEntreprise === roleId);
					entreprisesForRole.forEach($ => {
						let input = {
							role: roleId,
							entreprise: $ as EntrepriseTLR,
							intervenant: null,
							modificationContacts: false,
						} as IntervenantInput;
						inputs.push(input);
					});
				}
				this.intervenantsInputs$.next(inputs);

				// Initialisation du bloc acces
				let dateTimeDebut = acces.dateDebut;
				this.dateDebut$.next([dateTimeDebut[0], dateTimeDebut[1], dateTimeDebut[2]]);
				this.heureDebut$.next([dateTimeDebut[3], dateTimeDebut[4], dateTimeDebut[5]]);
				this.commentaire = acces.commentaire;
				this.lierIntervention = true;
				this.accesCegex$.next(acces.accesCegex === 1);
			}
		);

		/////////////////////////////////////
		// Initialisation - Mode: CREATION 
		
		// Recuperation de l'evenement via l'event bus
		let initAsCreationRequest$ = new Subject<void>();
		eventBusService.observe(Events.CreateAccesModalRequested, () => initAsCreationRequest$.next());
	
		// Enrichissement avec les donnnes necessaires		
		let initAsCreationWithUserDataRequest$ = combineLatest([initAsCreationRequest$, antenneOfUser$]).pipe(
			tap(clearViewBindings),
			map(
				(data) => data[1]
			)
		);
		
		// Initialisation des bindings de la vue pour le mode
		let initAsCreationFinalizationRequest$ = combineLatest([initAsCreationWithUserDataRequest$, staticDataLoaded$]).pipe(
			map(
				(data) => data[0]
			),
			tap(
				(antenneOfUser) => {
					this.mode = "creation";
					this.title = "Création d'accès";
					this.acces = null;
	
					// Initialiser le numero en premier, car le changement d'antenne peut l'alterer ensuite
					this.numero$.next('');
	
					// Initialisation du bloc perimetre (mode creation)
					this.antenne$.next(antenneOfUser);
					this.parc$.next(null);
					this.ouvrages$.next([]);
					this.ouvragesConcernes$.next([]);
	
					// Initialisation du bloc intervenants (mode creation)
					this.resetIntervenantsInputs();
	
					// Initialisation du bloc acces (mode creation)
					this.typeAcces$.next(null);
					this.resetDateCommands.next();
					this.commentaire = '';
					this.lierIntervention = true;
					this.accesCegex$.next(null);
	
					this.motifRefus = null;
					this.controleSecurite = null;
					this.isUserCac = false;
					this.isUserCc = false;
				}
			),
			map(
				(_data) => {}
			),
		);

		////////////////////////////////////////
		// Initialisation - Mode: MODIFICATION
		
		// Recuperation de l'evenement via l'event bus
		let initAsUpdateRequest$ = new Subject<number>();
		eventBusService.observe(Events.EditAccesModalRequested, (value: number) => initAsUpdateRequest$.next(value));
	
		// Enrichissement avec les donnnes necessaires		
		let initAsUpdateWithAccesDataRequest$ = initAsUpdateRequest$.pipe(
			tap(clearViewBindings),
			loadAccesOperator,
			loadFirstLevelDependenciesOperator,
			loadSecondLevelDependenciesOperator,
		);
		
		// Initialisation des bindings de la vue pour le mode
		let initAsUpdateFinalizationRequest$ = combineLatest([initAsUpdateWithAccesDataRequest$, staticDataLoaded$]).pipe(
			map(
				(data) => data[0]
			),
			tap(
				(accesData) => {
					this.mode = "update";
					this.title = "Modification d'accès";

					// Memoriser la reference de l'acces en cours de modification
					this.acces = accesData.acces;
				}
			),
			bindAccesData,
			_logTap("init numero for update"),
			map(
				(data) => {
					this.numero$.next(data.acces.numero);
				}
			),
		);

		////////////////////////////////////////
		// Initialisation - Mode: DUPLICATION
		
		// Recuperation de l'evenement via l'event bus
		let initAsDuplicateRequest$ = new Subject<number>();
		eventBusService.observe(Events.DuplicateAccesModalRequested, (value: number) => initAsDuplicateRequest$.next(value));
	
		// Enrichissement avec les donnnes necessaires		
		let initAsDuplicateWithAccesDataRequest$ = initAsDuplicateRequest$.pipe(
			loadAccesOperator,
			loadFirstLevelDependenciesOperator,
			loadSecondLevelDependenciesOperator,
		);
		
		// Initialisation des bindings de la vue pour le mode
		let initAsDuplicateFinalizationRequest$ = combineLatest([initAsDuplicateWithAccesDataRequest$, staticDataLoaded$]).pipe(
			map(
				(data) => data[0]
			),
			tap(
				(_accesData) => {
					this.mode = "creation";
					this.title = "Duplication d'accès";

					// Le but etant de fabriquer un nouvel acces, la reference d'acces bindee doit etre null
					this.acces = null;
				}
			),
			bindAccesData,
			tap(
				(_accesData) => {
					// Dans le cas d'une duplication on reset la date et heure de debut
					this.resetDateCommands.next();
				}
			),
			mergeMap(
				(accesData) => accesData.antenne ? accesService.getNumeroAcces(accesData.antenne.id) : of('')
			),
			_logTap("init numero for duplicate"),
			tap(
				(newNumero) => this.numero$.next(newNumero)
			),
			map(
				(_accesData) => {}
			),
		);

		/////////////////////////////////////////////////////
		// Initialisation - Finalisation de tous les modes

		merge(initAsCreationFinalizationRequest$, initAsUpdateFinalizationRequest$, initAsDuplicateFinalizationRequest$).subscribe(
			() => { 
				this.motifRefus = null;
				this.controleSecurite = null;
	
				if (allowLog) {
					console.log("edit-acces - loaded => true ");
				}
				this.loaded = true;
				this.displaySubject.next(true);
			}
		);

		isAccesDistant$.subscribe(
			(isAccesDistant) => {
				let inputs = this.intervenantsInputs$.getValue();
				if (isAccesDistant) {
					inputs.forEach(input => input.displayablePdpTypes = []);
				} else {
					inputs.forEach( input => {
							input.displayablePdpTypes = [];
							if (input && input.entreprise && input.entreprise.nom.toUpperCase().startsWith('ENGIE GREEN')) {
								input.displayablePdpTypes = [TypeDocument.SPECIFIQUE];
							} else {
								if (input.entreprise) {
									if (input.entreprise.documentApplicable === 'Both') {
										input.displayablePdpTypes = [TypeDocument.PA, TypeDocument.GENERAL, TypeDocument.SPECIFIQUE];
									} else if (input.entreprise.documentApplicable === 'PlanPrevention') {
										input.displayablePdpTypes = [TypeDocument.GENERAL, TypeDocument.SPECIFIQUE];
									} else if (input.entreprise.documentApplicable === 'PersonneAvertie') {
										input.displayablePdpTypes = [TypeDocument.PA];
									}
								}
							}						
						}
					);
				}
			}
		);
	}

	@Input() id = 'editAccesPopup';

	private resetIntervenantsInputs() {
		this.intervenantsInputs$.next([]);
		this.addIntervenantInput();
	}

	addIntervenantInput() {
		let inputs = this.intervenantsInputs$.getValue();
		inputs.push({
			role: inputs.length === 0 ? 0 : 1,
			entreprise: null,
			intervenant: null,
			modificationContacts: false,
		} as IntervenantInput);
		this.intervenantsInputs$.next(inputs);
	}

	removeIntervenantInput(index: number) {
		let inputs = this.intervenantsInputs$.getValue();
		inputs.splice(index, 1);
		this.intervenantsInputs$.next(inputs);
	}

	onEntrepriseChange(indexInput: number, entreprise: EntrepriseTLR | null) {
		let inputs = this.intervenantsInputs$.getValue();
		let input = inputs[indexInput];
		if (isSame(input.entreprise, entreprise)) {
			return;
		}

		input.entreprise = entreprise;
		input.intervenant = null;
		this.intervenantsInputs$.next(inputs);
	}

	onIntervenantChange(indexInput: number, intervenant: IntervenantTLR | null) {
		let inputs = this.intervenantsInputs$.getValue();
		let input = inputs[indexInput];
		if (isSame(input.intervenant, intervenant)) {
			return;
		}

		input.intervenant = intervenant;
		input.modificationContacts = false;

		this.updateContactDisplay(input);
		
		if (intervenant && intervenant.entrepriseId && this.entreprisesById) {
			const entrepriseIntervenant = this.entreprisesById.get(intervenant.entrepriseId);
			if (this.typeAcces$.getValue() === 'A_DISTANCE') {
				input.displayablePdpTypes = [];
			} else {
				if (entrepriseIntervenant && entrepriseIntervenant.nom.toUpperCase().startsWith('ENGIE GREEN')) {
					input.displayablePdpTypes = [TypeDocument.SPECIFIQUE];
				} else {
					if (entrepriseIntervenant.documentApplicable === 'Both') {
						input.displayablePdpTypes = [TypeDocument.PA, TypeDocument.GENERAL, TypeDocument.SPECIFIQUE];
					} else if (entrepriseIntervenant.documentApplicable === 'PlanPrevention') {
						input.displayablePdpTypes = [TypeDocument.GENERAL, TypeDocument.SPECIFIQUE];
					} else if (entrepriseIntervenant.documentApplicable === 'PersonneAvertie') {
						input.displayablePdpTypes = [TypeDocument.PA];
					}
				}
			}
		}

		// Si l'utilisateur selectionne un intervenant avant de selectionner son entreprise,
		// remplir automatiquement l'entreprise		
		if (intervenant && !input.entreprise && this.entreprisesById) {
			// NB: ici on sait normalement que entrepriseId n'est pas null
			// car les intervenants ont ete recuperes a travers les entreprises
			let entrepriseId = intervenant.entrepriseId as number;
			let entreprise = this.entreprisesById.get(entrepriseId);
			input.entreprise = entreprise;
		}

		this.intervenantsInputs$.next(inputs);
		
		for (let i = 0; i < inputs.length; i++){
			this.determinerEtAfficherAlertes(i);
		}

	}

	private updateContactDisplay(input: IntervenantInput) {
		let intervenant = input.intervenant;

		if (intervenant) {
			let indexIT = this.indicatifTelephoneOptions.findIndex($ => $.dialCode === (intervenant as IntervenantTLR).indicatifTelephone);
			input.indicatifTelephone = this.indicatifTelephoneOptions[indexIT];
			input.telephone = this.nonNull(intervenant.telephone);
			input.email = this.nonNull(intervenant.email);
		}
		if (this.typeAcces$.getValue() === 'A_DISTANCE') {
			input.displayablePdpTypes = [];
		} else {
			if (input.entreprise) {
				if (input.entreprise.nom.toUpperCase().startsWith('ENGIE GREEN')) {
					input.displayablePdpTypes = [TypeDocument.SPECIFIQUE];
				} else {
					if (input.entreprise.documentApplicable === 'Both') {
						input.displayablePdpTypes = [TypeDocument.PA, TypeDocument.GENERAL, TypeDocument.SPECIFIQUE];
					} else if (input.entreprise.documentApplicable === 'PlanPrevention') {
						input.displayablePdpTypes = [TypeDocument.GENERAL, TypeDocument.SPECIFIQUE];
					} else if (input.entreprise.documentApplicable === 'PersonneAvertie') {
						input.displayablePdpTypes = [TypeDocument.PA];
					}
				}				
			}
		}
	}

	onCancelModificationContact(input: IntervenantInput) {
		input.modificationContacts = false;
		this.updateContactDisplay(input);
	}
	
	private nonNull(value: string | null): string {
		return value ? value : '';
	}

	onInputModificationContact(input: IntervenantInput) {
		input.modificationContacts = false;

		if (!input.intervenant) {
			throw new Error("input.intervenant ne devrait pas etre null a ce point");
		}

		let intervenant = input.intervenant;
		let dialCode = input.indicatifTelephone ? input.indicatifTelephone.dialCode : null;

		this.intervenantService.updateContactInfos(
			intervenant.id,
			dialCode,
			input.telephone,
			input.email,
		).subscribe(
			(_) => {
				intervenant.indicatifTelephone = dialCode;
				intervenant.telephone = input.telephone;
				intervenant.email = input.email;
			}
		)
	}

	close() {
		this.displaySubject.next(false);
	}

	createNewPlannedAcces(withDemandeValidation: boolean) {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.olderThan(dateHeureDebut, 30)) {
			specificErrors.push("Vous ne pouvez pas plannifier un acces avec un debut déjà depassé depuis plus de 30 minutes.");
		}

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		// Cree l'objet de commande pour la creation de l'acces
		let command = this.fillCreateCommandObject(new CreationPlannedAccesOrder());
		command.demanderValidation = withDemandeValidation;
		
		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.createAsPlanned(command).subscribe(
			(id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);

				// S'il faut associer des interventions au dit acces...
				if (this.lierIntervention) {
					// ... appeller la fenetre d'association d'un acces avec des interventions
					this.eventBusService.emit(Events.AssociationOperationsModalRequested, id);
				}
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la création de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de création de l'accès", error);
			}
		);
	}
	
	createNewGrantedAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.inFuture(dateHeureDebut)) {
			specificErrors.push("Vous ne pouvez pas valider un accès plus de 15 minutes dans le futur.");
		}

		if (this.intervenantsInputs$.getValue().some($ => $.entreprise !== null && $.intervenant === null)) {
			specificErrors.push("Vous ne pouvez pas valider un accès sans préciser l'intervenant de chaque entreprise.");
		}

		// NB: le controle de securite est optionnel.

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		// Confier la suite des operations au checker de coactivite adapte
		let parcNN = notNull(this.parc$.getValue(), "parc");
		
		let decisionSubject = new Subject<CoactiviteDecision>();
		decisionSubject.subscribe(
			(decision) => {
				if (decision === CoactiviteDecision.AUTHORIZE || decision === CoactiviteDecision.AUTHORIZE_WITH_FORCE) {
					this.persistNewGrantedAcces();
				} else if (decision === CoactiviteDecision.DENY) {
					this.motifRefus = 4;
					this.persistNewDeniedAcces();
				} else if (decision === CoactiviteDecision.ASK_VALIDATION) {
					this.createNewPlannedAcces(true);
				}
			}
		);

		let energie = parcNN.energie;
		if (energie === 'Solaire') {
			this.checkCoactiviteSolaireCommands.next({
				accesId: -1,
				ouvragesIds: this.ouvrages$.getValue().map($ => $.id),
				decisionSubject: decisionSubject,
				isUserCac: this.isUserCac,
				isUserCc: this.isUserCc
			});
		} else if (energie === 'Eolienne') {
			// NB: dans un parc eolien, il n'y a normalement qu'un seul ouvrage
			this.checkCoactiviteEolienCommands.next({
				accesId: -1,
				accesCegex: notNull(this.accesCegex$.getValue(), "accesCegex"),
				ouvrageId: notNull(this.ouvrages$.getValue(), "ouvrage")[0].id,
				decisionSubject: decisionSubject,
				isUserCac: this.isUserCac,
				isUserCc: this.isUserCc
			});
		}

		// On arrete le traitement ici en attendant la reponse du checker
	}
	
	private demanderValidation(accesId: number) {
		this.accesService.demanderValidation(accesId).pipe(
			catchError(
				(response: HttpErrorResponse) => {
					if (response.error && response.error.message) {
						alert(response.error.message);
					} else {
						alert("Erreur lors de la demande de validation de cet accès (" + response.status + ")");
					}
					throw new Error();
				}
			)
		).subscribe(
			(_resultat: any) => {
				console.log('demande validation poussée: ', _resultat);
			}
		);
	}

	private persistNewGrantedAcces() {
		// Cree l'objet de commande pour la creation de l'acces
		let order = this.fillCreateCommandObject(new CreationGrantedAccesOrder());
		order.controleSecurite = this.controleSecurite;
		let now = this.conversionService.toLocalDateTime(new Date());
		if (now != null) {
			order.dateAutorisation = now;
		}

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.createAsGranted(order).subscribe(
			(id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);

				// S'il faut associer des interventions au dit acces...
				if (this.lierIntervention) {
					// ... appeller la fenetre d'association d'un acces avec des interventions
					this.eventBusService.emit(Events.AssociationOperationsModalRequested, id);
				}
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la création de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de création de l'accès", error);
			}
		);
	}

	createNewDeniedAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.inFuture(dateHeureDebut)) {
			specificErrors.push("Vous ne pouvez pas refuser un accès plus de 15 minutes dans le futur.");
		}

		if (this.intervenantsInputs$.getValue().some($ => $.entreprise !== null && $.intervenant === null)) {
			specificErrors.push("Vous ne pouvez pas refuser un accès sans préciser l'intervenant de chaque entreprise.");
		}

		if (this.motifRefus == null) {
			specificErrors.push("Veuillez sélectionner un motif de refus.");
		}

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		this.persistNewDeniedAcces();
	}

	private persistNewDeniedAcces() {
		// Cree l'objet de commande pour la creation de l'acces
		let order = this.fillCreateCommandObject(new CreationDeniedAccesOrder());
		order.motifRefus = notNull(this.motifRefus, "motifRefus");
		let now = this.conversionService.toLocalDateTime(new Date());
		if (now != null) {
			order.dateRefus = now;
		}

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.createAsDenied(order).subscribe(
			(_id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la création de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de création de l'accès", error);
			}

		);
	}

	updateAcces() {
		let acces = this.acces;
		if (!acces) {
			throw new Error("Acces absent lors d'une tentative de mise a jour");
		}

		if (acces.etat <= 1) {
			this.updatePlannedAcces();
		} else if (acces.etat === 2) {
			this.updateGrantedAcces();
		} else {
			throw new Error("Tentative de mise a jour d'un acces ni plannifie ni en cours");
		}
	}

	private updatePlannedAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		// Cas: L'acces est actuellement juste pannifie
		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.olderThan(dateHeureDebut, 30)) {
			specificErrors.push("Vous ne pouvez pas plannifier un acces avec un debut déjà depassé depuis plus de 30 minutes.");
		}

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		// Cree l'objet de commande pour la creation de l'acces
		let order = this.fillUpdatePlannedCommandObject(new UpdatePlannedAccesOrder());

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.updatePlanned(order).subscribe(
			(_id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la modification de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de modification de l'accès", error);
			}
		);
	}

	private updateGrantedAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		if (this.intervenantsInputs$.getValue().some($ => $.entreprise !== null && $.intervenant === null)) {
			specificErrors.push("Vous ne pouvez pas enregistrer un accès validé sans préciser l'intervenant de chaque entreprise.");
		}

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		let order = this.fillUpdateCommandObject(new UpdateGrantedAccesOrder());

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.updateGranted(order).subscribe(
			(_id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la modification de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de modification de l'accès", error);
			}
		);
	}

	updateAndGrantAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.inFuture(dateHeureDebut)) {
			specificErrors.push("Vous ne pouvez pas valider un accès plus de 15 minutes dans le futur.");
		}

		if (this.intervenantsInputs$.getValue().some($ => $.entreprise !== null && $.intervenant === null)) {
			specificErrors.push("Vous ne pouvez pas valider un accès sans préciser l'intervenant de chaque entreprise.");
		}

		// NB: le controle de securite est optionnel.

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		// Confier la suite des operations au checker de coactivite adapte
		let acces= notNull(this.acces, "acces");
		let parc = notNull(this.parc$.getValue(), "parc");
		
		let decisionSubject = new Subject<CoactiviteDecision>();
		decisionSubject.subscribe(
			(decision) => {
				if (decision === CoactiviteDecision.AUTHORIZE || decision === CoactiviteDecision.AUTHORIZE_WITH_FORCE) {
					this.persistUpdateAndGrantAcces();
				} else if (decision === CoactiviteDecision.DENY) {
					this.motifRefus = 4;
					this.persistUpdateAndDenyAcces();
				} else if (decision === CoactiviteDecision.ASK_VALIDATION) {
					this.demanderValidation(acces.id);
				}
			}
		);

		let energie = parc.energie;
		if (energie === 'Solaire') {
			this.checkCoactiviteSolaireCommands.next({
				accesId: acces.id,
				ouvragesIds: this.ouvrages$.getValue().map($ => $.id),
				decisionSubject: decisionSubject,
				isUserCac: this.isUserCac,
				isUserCc: this.isUserCc
			});
		} else if (energie === 'Eolienne') {
			// NB: dans un parc eolien, il n'y a normalement qu'un seul ouvrage
			this.checkCoactiviteEolienCommands.next({
				accesId: acces.id,
				accesCegex: notNull(this.accesCegex$.getValue(), "accesCegex"),
				ouvrageId: notNull(this.ouvrages$.getValue(), "ouvrage")[0].id,
				decisionSubject: decisionSubject,
				isUserCac: this.isUserCac,
				isUserCc: this.isUserCc
			});
		}

		// On arrete le traitement ici en attendant la reponse du checker
	}

	private persistUpdateAndGrantAcces() {
		// Cree l'objet de commande pour la modification et validation de l'acces
		let order = this.fillUpdatePlannedCommandObject(new UpdateAndGrantAccesOrder());
		order.dateAutorisation = this.conversionService.toMandatoryLocalDateTime(new Date());
		order.controleSecurite = this.controleSecurite;

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.updateAndGrant(order).subscribe(
			(id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);

				// Appel des associations d'operations
				this.eventBusService.emit(Events.AssociationOperationsModalRequested, id);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la modification de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de modification de l'accès", error);
			}
		);
	}

	updateAndDenyAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.inFuture(dateHeureDebut)) {
			specificErrors.push("Vous ne pouvez pas refuser un accès plus de 15 minutes dans le futur.");
		}

		if (this.intervenantsInputs$.getValue().some($ => $.entreprise !== null && $.intervenant === null)) {
			specificErrors.push("Vous ne pouvez pas refuser un accès sans préciser l'intervenant de chaque entreprise.");
		}

		if (this.motifRefus == null) {
			specificErrors.push("Veuillez sélectionner un motif de refus.");
		}

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		this.persistUpdateAndDenyAcces();
	}
	
	private persistUpdateAndDenyAcces() {
		// Cree l'objet de commande pour la modification et refus de l'acces
		let order = this.fillUpdatePlannedCommandObject(new UpdateAndDenyAccesOrder());
		order.motifRefus = notNull(this.motifRefus, "motifRefus");

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.updateAndDeny(order).subscribe(
			(_id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la modification de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de modification de l'accès", error);
			}
		);
	}

	private olderThan(date: LocalDateTime, nbMinutes: number): boolean {
		let dateRef = new Date();
		dateRef.setMinutes(dateRef.getMinutes() - nbMinutes);
		let dateDebutJS = this.conversionService.fromMandatoryLocalDateTime(date);
		return dateDebutJS.getTime() < dateRef.getTime();
	}

	// Determine si une date est plus de 15mn dans le futur
	private inFuture(date: LocalDateTime): boolean {
		let dateRef = new Date();
		dateRef.setMinutes(dateRef.getMinutes() + 15);
		let dateDebutJS = this.conversionService.fromMandatoryLocalDateTime(date);
		return dateDebutJS.getTime() > dateRef.getTime();
	}

	// Affiche les messages d'erreur de validation
	private displayValidationErrors(specificErrors: string[]): boolean {
		let errors: string[] = [...this.errorMessages, ...specificErrors];

		if (errors.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Enregistrement impossible',
					messages: errors
				}
			});

			return true;
		}

		return false;
	}

	private fillCreateCommandObject<T extends CreationAccesOrder>(order: T): T {
		// Integrer les valeur des saisies en Observable
		order.parcId = notNull(this.parc$.getValue(), "parc").id;
		order.ouvrageIds = this.ouvrages$.getValue().map($ => $.id);
		order.ouvragesConcernesIds = this.ouvragesConcernes$.getValue().map($ => $.id);

		this.fillBasicCommandProperties(order);
		
		order.numero = this.numero$.getValue();
		order.typeAcces = notNull(this.typeAcces$.getValue(), "typeAcces");
		order.dateDebut = notNull(this.dateHeureDebut$.getValue(), "dateDebut");
		order.accesCegex = notNull(this.accesCegex$.getValue(), "accesCegex");

		return order;
	}

	private fillUpdateCommandObject<T extends UpdateAccesOrder>(order: T): T {
		order.id = notNull(this.acces, "acces").id;
		
		this.fillBasicCommandProperties(order);

		return order;
	}
	
	private fillBasicCommandProperties<T extends BasicAccesOrder>(order: T): T {
		let inputs = this.intervenantsInputs$.getValue();
		let intervenantsInputs = inputs.filter($ => $.intervenant);
		let participantsIntervenants = intervenantsInputs.map($ => ({
			role: $.role,
			id: notNull($.intervenant, "intervenant").id,
		} as ParticipantAcces));
		order.intervenants = participantsIntervenants;
		let entrepriseInputs = inputs.filter($ => !$.intervenant);
		let participantsEntreprises = entrepriseInputs.map($ => ({
			role: $.role,
			id: notNull($.entreprise, "entreprise").id,
		} as ParticipantAcces));
		order.entreprises = participantsEntreprises;

		order.commentaire = this.commentaire;
		order.astreinte = notNull(this.astreinte$.getValue(), "astreinte");

		return order;
	}

	private fillUpdatePlannedCommandObject<T extends UpdatePlannedAccesOrder>(order: T): T {
		this.fillUpdateCommandObject(order);
		order.ouvragesConcernesIds = this.ouvragesConcernes$.getValue().map($ => $.id);
		order.typeAcces = notNull(this.typeAcces$.getValue(), "typeAcces");
		order.dateDebut = notNull(this.dateHeureDebut$.getValue(), "dateDebut");
		order.accesCegex = notNull(this.accesCegex$.getValue(), "accesCegex");
		return order;
	}
	
	public allIntervenantsFilled(): boolean {
		if (this.intervenantsInputs$.getValue() && this.intervenantsInputs$.getValue().length > 0) {
			return !this.intervenantsInputs$.getValue().some($ => $.intervenant === null)
		}
		return false;
	}
	
	public atLeastOneOuvrageSelected(): boolean {
		return this.ouvrages$.getValue() && this.ouvrages$.getValue().length > 0;
	}
	
	roleHasChanged(index: number) {
		console.log('on a choisi ' + this.intervenantRoles[index] + ', calculer les alertes');
		this.determinerEtAfficherAlertes(index);
	}
	
	determinerEtAfficherAlertes(index: number) {
		let intervenants = this.intervenantsInputs$.getValue();
		let nbPersonnesInEquipe = intervenants.length;
		let chosenIntervenant: IntervenantTLR | null = intervenants[index].intervenant;
		let chosenRole = intervenants[index].role;
		let energieParc = this.parc$.getValue()?.energie;
		let nomIntervenant = chosenIntervenant?.nom;
		let prenomIntervenant = chosenIntervenant?.prenom;
		
		if (chosenIntervenant != null && chosenRole != null) {
			this.typeHabilitationService.getWithQualCodesByIntervenant(chosenIntervenant.id, true).subscribe(
				(typesHabilitations: TypeHabilitationWithQualCodesDTO[]) => {
					typesHabilitations.forEach(habilitation => {
						if (habilitation.dateFin) {
							let timestampFin  = this.conversionService.fromMandatoryLocalDate(habilitation.dateFin).getTime();
							habilitation.expirationStatus = new Date().getTime() <= timestampFin ? ExpirationStatus.VALID : ExpirationStatus.EXPIRED;
						} else {
							habilitation.expirationStatus = ExpirationStatus.EXPIRED;
						}
					});
					console.log('on a recupéré les habilitations: ', typesHabilitations);
					console.log('je verifie si les RG sont respectés');
					// a chaque violation de RG afficher une alerte
					let alertMessage = '';
					switch (chosenRole) {
						
						case 2: // Chargé de travaux
						  alertMessage = 'Pour être chargé de travaux, l\'intervenant ' + nomIntervenant + ' ' + prenomIntervenant +' doit être habilité B2(V) et H2(V)';
						  this.intervenantsInputs$.getValue()[index].shouldBlink = 'blink_icon';

						  const hasB2OrB2V = typesHabilitations.some(th =>
						    	th.qualificationType === 1 &&
						    	(th.qualificationCodes.includes('B2') || th.qualificationCodes.includes('B2V')) &&
						    	th.expirationStatus === ExpirationStatus.VALID
						  );

						  const hasH2OrH2V = typesHabilitations.some(th =>
						    	th.qualificationType === 2 &&
						    	(th.qualificationCodes.includes('H2') || th.qualificationCodes.includes('H2V')) &&
						    	th.expirationStatus === ExpirationStatus.VALID
						  );

						  if (hasB2OrB2V && hasH2OrH2V) {
						    	alertMessage = '';
						    	this.intervenantsInputs$.getValue()[index].shouldBlink = '';
						  }
						  break;
						
						case 3 : // Chargé de consignation
						
						// habilité BR si le parc est éolien et BR PV si le parc est solaire si l’intervenant est seul, Chargé de consignation
						if(nbPersonnesInEquipe === 1){
							this.intervenantsInputs$.getValue()[index].shouldBlink = 'blink_icon';
							typesHabilitations.forEach(th => {
								if(energieParc === 'Eolienne'){
									alertMessage ='Pour être Chargé de consignation, l\'intervenant ' + nomIntervenant + ' ' + prenomIntervenant + ' doit être habilité  BR lorsque le parc est éolien et qu\'il travaille seul';
									if (th.qualificationType === 1 && th.qualificationCodes.some($ => $ === 'BR')) {
										// si il possede un BR valid retirer le message d'alerte
										if (th.expirationStatus === ExpirationStatus.VALID) {
											alertMessage = '';
											this.intervenantsInputs$.getValue()[index].shouldBlink = '';
										}
									}
								}
								if(energieParc === 'Solaire'){
									alertMessage ='Pour être Chargé de consignation, l\'intervenant ' + nomIntervenant + ' ' + prenomIntervenant + ' doit être habilité  BR PV lorsque le parc est solaire et qu\'il travaille seul';
									if (th.qualificationType === 1 && th.qualificationCodes.some($ => $ === 'BR PV')){
										// si il possede un BR PV valid retirer le message d'alerte
										if (th.expirationStatus === ExpirationStatus.VALID) {
											alertMessage = '';
											this.intervenantsInputs$.getValue()[index].shouldBlink = '';
										}
									}
								}	
							});
						}
						
						if(nbPersonnesInEquipe > 1 ){ 
							alertMessage = 'Pour être chargé de consignation, l\'intervenant '+ nomIntervenant + ' ' + prenomIntervenant +' doit être habilité  BC ou HC, s\'il travaille en équipe';
							this.intervenantsInputs$.getValue()[index].shouldBlink = 'blink_icon';
							typesHabilitations.forEach(th => {
								
									if (th.qualificationType === 1 && th.qualificationCodes.some($ => $ === 'BC')) {
										// si il possede un BC valid retirer le message d'alerte
										if (th.expirationStatus === ExpirationStatus.VALID) {
											alertMessage = '';
											this.intervenantsInputs$.getValue()[index].shouldBlink = '';
										}
									} else if (th.qualificationType === 2 && th.qualificationCodes.some($ => $ === 'HC')){
										// si il possede un HC valid retirer le message d'alerte
										if (th.expirationStatus === ExpirationStatus.VALID) {
											alertMessage = '';
											this.intervenantsInputs$.getValue()[index].shouldBlink = '';
										}
									}
								
							});
						}
						break;
						
						case 4 : // chargé de travaux et de consignation
							// habilité B2, H2 et BR si le parc est solaire ou B2(V), H2(V) et BR PV si le parc est solaire si l’intervenant est seul, chargé de travaux et de consignation 
							if (nbPersonnesInEquipe === 1) {
 								this.intervenantsInputs$.getValue()[index].shouldBlink = 'blink_icon';
								
							  	if (energieParc === 'Eolienne') {
									alertMessage = 'Pour être chargé de travaux et de consignation, l\'intervenant ' + nomIntervenant + ' ' + prenomIntervenant +' doit être habilité B2(V), H2(V) et BR lorsque le parc est éolien';
							    	const hasType1 = typesHabilitations.some(th =>
							      	th.qualificationType === 1 &&
							      	(th.qualificationCodes.includes('B2') || th.qualificationCodes.includes('B2V')) &&
							      	th.qualificationCodes.includes('BR') &&
							      	th.expirationStatus === ExpirationStatus.VALID
							    );

							    const hasType2 = typesHabilitations.some(th =>
							      	th.qualificationType === 2 &&
							      	(th.qualificationCodes.includes('H2') || th.qualificationCodes.includes('H2V')) &&
							    	th.expirationStatus === ExpirationStatus.VALID
							    );

							    if (hasType1 && hasType2) {
							      	alertMessage = '';
							      	this.intervenantsInputs$.getValue()[index].shouldBlink = '';
							    }
							  }

							  if (energieParc === 'Solaire') {
								alertMessage = 'Pour être chargé de travaux et de consignation, l\'intervenant ' + nomIntervenant + ' ' + prenomIntervenant +' doit être habilité B2(V), H2(V) et BR PV lorsque le parc est solaire';
							  	const hasType1 = typesHabilitations.some(th =>
							    	th.qualificationType === 1 &&
							      	(th.qualificationCodes.includes('B2') || th.qualificationCodes.includes('B2V')) &&
							      	th.qualificationCodes.includes('BR PV') &&
							      	th.expirationStatus === ExpirationStatus.VALID
							    );

							    const hasType2 = typesHabilitations.some(th =>
							      	th.qualificationType === 2 &&
							      	(th.qualificationCodes.includes('H2') || th.qualificationCodes.includes('H2V'))  &&
							      	th.expirationStatus === ExpirationStatus.VALID
							    );

							    if (hasType1 && hasType2) {
							      	alertMessage = '';
							      	this.intervenantsInputs$.getValue()[index].shouldBlink = '';
							    }
							  }
							}

							// habilité B2, H2, BC et HC si l’intervenant est en équipe, chargé de travaux et de consignation 
							if(nbPersonnesInEquipe > 1){
								alertMessage = 'Pour être Chargé de travaux et de consignation, l\'intervenant ' + nomIntervenant + ' ' + prenomIntervenant +' doit être habilité B2(V), H2(V), BC et HC, s\'il travaille en équipe';
								this.intervenantsInputs$.getValue()[index].shouldBlink = 'blink_icon';
								typesHabilitations.forEach(th => {
									const hasType1 = typesHabilitations.some(th =>
									  	th.qualificationType === 1 &&
									  	(th.qualificationCodes.includes('B2') || th.qualificationCodes.includes('B2V')) &&
									  	th.qualificationCodes.includes('BC') &&
									  	th.expirationStatus === ExpirationStatus.VALID
									);

									const hasType2 = typesHabilitations.some(th =>
									  	th.qualificationType === 2 &&
									  	(th.qualificationCodes.includes('H2') || th.qualificationCodes.includes('H2V')) &&
									  	th.qualificationCodes.includes('HC') &&
									  	th.expirationStatus === ExpirationStatus.VALID
									);

									if (hasType1 && hasType2) {
									  	alertMessage = '';
									  	this.intervenantsInputs$.getValue()[index].shouldBlink = '';
									}
								});
							}
												
						break;
						
						default:
							// rien à faire ici
					}
					if (alertMessage) {
						alert(alertMessage);
					}
									
					// si j'ai une alerte et que que je dois blinker une icone
					// changer la valeur de l'input blinkIcone dans inervenant-status
				}
			);
		}
	}
	
}